import React from 'react';
const { useState, useMemo } = React;
import { F } from '../../utils.jsx';
import { contactNameOf } from '../../personQuotes.js';
import { chargesCall, newChargeKey } from '../../chargesClient.js';
import { CHARGE } from '../../billingSchema.js';
import { acceptedProposalsOf, payerOfCharge, samePayer, cents, money } from '../../payments.js';
import ChargeForm from './ChargeForm.jsx';
import PlanResolution from './PlanResolution.jsx';
import { fmt, payerLabel } from './ChargesList.jsx';

// «Registrar pago»: choose who pays, see their accepted proposals (accepted by the client) with what is
// receivable and their pending charges, choose what this payment covers, enter amount, currency, day,
// method and reference, and see before confirming how it is spread, what stays unapplied and what is
// still pending. One real payment is registered once, even when it covers several installments
// (charges.mts checks everything again). A proposal without a plan offers to generate its receivable.
const muted = { fontSize: 12, color: 'var(--globant-muted)' };
const norm = (v) => String(v || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
const todayDay = () => new Date().toISOString().slice(0, 10);
const kindLabel = (f) => (f[CHARGE.installmentKind] === 'installment' ? `cuota ${f[CHARGE.installmentNumber]}/${f[CHARGE.installmentsTotal]}` : f[CHARGE.installmentKind] === 'advance' ? 'anticipo' : f[CHARGE.installmentKind] === 'balance' ? 'saldo' : 'pago completo');

export default function PaymentForm({ stakeholders = [], accounts = [], intakes = [], billing, preset = null, onDone, onCancel, onBillingChanged }) {
  const [key] = useState(newChargeKey); // one key per registration, reused on retries
  const [payerType, setPayerType] = useState(preset?.type || '');
  const [payerId, setPayerId] = useState(preset?.id || '');
  const [q, setQ] = useState('');
  const [sel, setSel] = useState({}); // chargeId → amount (string)
  const [amount, setAmount] = useState('');
  const [currencyFree, setCurrencyFree] = useState('USD');
  const [receivedOn, setReceivedOn] = useState(todayDay());
  const [method, setMethod] = useState('');
  const [reference, setReference] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [planFor, setPlanFor] = useState(null); // an accepted proposal to generate its receivable

  const charges = billing?.charges || [];
  const payer = { type: payerType, id: payerId };
  const payerName = payerType === 'company' ? F(accounts.find(a => a.id === payerId), 'Account Name') : contactNameOf(stakeholders.find(s => s.id === payerId));
  const matches = useMemo(() => {
    const w = norm(q).trim(); if (!w) return [];
    return payerType === 'company' ? accounts.filter(a => norm(F(a, 'Account Name')).includes(w)).slice(0, 8) : stakeholders.filter(s => norm(contactNameOf(s)).includes(w)).slice(0, 8);
  }, [q, payerType]);
  const proposals = useMemo(() => (payerId ? acceptedProposalsOf(payer, { intakes, ...billing }) : []), [payerId, payerType, billing, intakes]);
  // Pending charges of this payer only (never another payer's: the employer link opens no personal finances)
  const pending = useMemo(() => (payerId ? charges.filter(c => c.fields?.[CHARGE.status] === 'open' && samePayer(payerOfCharge(c.fields || {}), payer) && (c.state?.pending || 0) > 0)
    .sort((a, b) => String(a.fields?.[CHARGE.dueOn] || '').localeCompare(String(b.fields?.[CHARGE.dueOn] || ''))) : []), [payerId, payerType, charges]);
  const chosen = pending.filter(c => sel[c.id] !== undefined);
  const currencies = [...new Set(chosen.map(c => c.fields?.[CHARGE.currency]))];
  const currency = currencies[0] || currencyFree;
  const appliedC = chosen.reduce((s, c) => s + cents(Number(sel[c.id]) || 0), 0);
  const amountC = cents(Number(amount) || 0);
  const unappliedC = amountC - appliedC;
  const quoteName = (id) => intakes.find(r => r.id === id)?.fields?.['Name'] || '';
  const toggle = (c) => setSel(s => { const n = { ...s }; if (n[c.id] !== undefined) delete n[c.id]; else n[c.id] = String(c.state?.pending || ''); return n; });
  // Spread the amount over the pending charges, oldest due first
  const spread = () => {
    let left = amountC; const n = {};
    for (const c of pending.filter(x => !currencies.length || x.fields?.[CHARGE.currency] === currency)) {
      if (left <= 0) break;
      const take = Math.min(left, cents(c.state?.pending || 0));
      if (take > 0) { n[c.id] = String(money(take)); left -= take; }
    }
    setSel(n);
  };
  const problems = [
    currencies.length > 1 && 'Los cobros elegidos tienen monedas distintas: un pago es de una sola moneda.',
    unappliedC < 0 && 'Lo que aplicás supera el importe del pago.',
    chosen.some(c => cents(Number(sel[c.id]) || 0) > cents(c.state?.pending || 0)) && 'Un importe supera lo pendiente de su cobro.',
    chosen.some(c => !(Number(sel[c.id]) > 0)) && 'Cada cobro elegido necesita un importe mayor que 0.',
  ].filter(Boolean);
  const ready = payerId && amountC > 0 && receivedOn && !problems.length;

  const submit = async () => {
    setBusy(true); setErr('');
    try {
      const payment = {
        payerType, payerStakeholderId: payerType === 'person' ? payerId : '', payerAccountId: payerType === 'company' ? payerId : '',
        amount: money(amountC), currency, receivedOn, method, reference, key,
        allocations: chosen.map(c => ({ chargeId: c.id, amount: Number(sel[c.id]) })),
      };
      const r = await chargesCall('recordPayment', { payment });
      onDone(r);
    } catch (e) { setErr(e.message); } // what was typed stays; a retry reuses the key
    setBusy(false);
  };

  if (planFor) {
    const p = planFor;
    const planIndex = new Set(p.charges.map(c => c.fields?.[CHARGE.planKey] || c.fields?.[CHARGE.key])).size + 1;
    return <ChargeForm stakeholders={stakeholders} accounts={accounts} quote={p.quote} versionNumber={p.number}
      planKey={`q-${p.quote.id}-p${planIndex}`} suggestedTotal={p.notPlanned} recognized={p.earlierCollected} keptPending={p.keptPending}
      onDone={() => { setPlanFor(null); onBillingChanged?.(); }} onCancel={() => setPlanFor(null)} />;
  }

  return (
    <div className="card" style={{ marginBottom: 12, display: 'grid', gap: 10 }}>
      <b style={{ fontSize: 14 }}>Registrar pago</b>
      {!preset && (
        <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', fontSize: 13 }}>
          <label><input type="radio" name="pf-payer" checked={payerType === 'person'} onChange={() => { setPayerType('person'); setPayerId(''); setSel({}); }} /> Paga una persona</label>
          <label><input type="radio" name="pf-payer" checked={payerType === 'company'} onChange={() => { setPayerType('company'); setPayerId(''); setSel({}); }} /> Paga una empresa</label>
        </div>
      )}
      {payerType && !payerId && (
        <div>
          <input className="input-field" style={{ fontSize: 12, width: '100%', maxWidth: 360 }} value={q} onChange={e => setQ(e.target.value)} placeholder={payerType === 'company' ? 'Buscar la empresa que paga' : 'Buscar la persona que paga'} aria-label="Buscar pagador" />
          {matches.map(m => <button key={m.id} type="button" className="action-btn btn-ghost" style={{ display: 'block', fontSize: 12, marginTop: 4, textAlign: 'left' }} onClick={() => { setPayerId(m.id); setQ(''); }}>{payerType === 'company' ? F(m, 'Account Name') : contactNameOf(m)}</button>)}
        </div>
      )}
      {payerId && (
        <>
          <div style={{ fontSize: 13 }}>Paga: <b>{payerName}</b>{!preset && <> <button type="button" className="action-btn btn-ghost" style={{ fontSize: 11 }} onClick={() => { setPayerId(''); setSel({}); }}>Cambiar</button></>}</div>

          <div>
            <div style={{ fontSize: 12, fontWeight: 600 }}>Propuestas aceptadas</div>
            {!proposals.length && <div style={muted}>Sin propuestas aceptadas por el cliente.</div>}
            {proposals.map(p => (
              <div key={p.quote.id} style={{ fontSize: 12, padding: '4px 0', borderBottom: '1px solid var(--globant-border)' }}>
                <b>{p.quote.fields?.['Name'] || 'Propuesta'}</b> (v{p.number}) · acordado {fmt(p.agreed, p.currency)}{p.monthly ? ` + ${fmt(p.monthly, p.currency)}/mes` : ''}
                {p.additionalPending > 0 && <div style={muted}>Además, contratación adicional independiente: pendiente {fmt(p.additionalPending, p.currency)}.</div>}
                {p.hasPlan ? <> · aplicado {fmt(p.collected, p.currency)} · <b>saldo por cobrar {p.payable === null ? 'por resolver' : fmt(p.payable, p.currency)}</b></> : <> · sin cuenta por cobrar todavía</>}
                {p.earlierCollected > 0 && <div style={muted}>Ya cobrado en versiones anteriores: {fmt(p.earlierCollected, p.currency)} (queda en esas versiones; no se vuelve a pedir).</div>}
                {p.legacyPayment && <div style={{ fontSize: 12, color: '#9A6212' }}>Esta versión ya tenía un pago registrado antes de usar cobros ({p.legacyPayment === 'partial' ? 'parcial' : p.legacyPayment === 'pending' ? 'pendiente' : 'completado'}), sin importe. No se convierte en dinero: revisá cuánto se cobró antes de generar o cobrar el saldo.</div>}
                {p.earlierLegacy.length > 0 && <div style={muted}>La v{p.earlierLegacy.join(', v')} tiene un pago anterior registrado sin importe: revisá el saldo antes de cobrar.</div>}
                <PlanResolution proposal={p} billing={billing} intakes={intakes} onChanged={onBillingChanged} />
                {!p.hasPlan && p.canPlan && <button type="button" className="action-btn btn-ghost" style={{ fontSize: 11, marginTop: 2 }} onClick={() => setPlanFor(p)}>Generar cuenta por cobrar</button>}
              </div>
            ))}
          </div>

          <div>
            <div style={{ fontSize: 12, fontWeight: 600 }}>¿Qué cubre este pago?</div>
            {!pending.length && <div style={muted}>Sin cobros pendientes de este pagador. Podés registrar el pago igual: queda sin aplicar.</div>}
            {pending.map(c => {
              const f = c.fields || {};
              const on = sel[c.id] !== undefined;
              return (
                <div key={c.id} style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap', fontSize: 12, padding: '3px 0' }}>
                  <label style={{ flex: '1 1 240px' }}><input type="checkbox" checked={on} onChange={() => toggle(c)} /> {f[CHARGE.concept]} · {kindLabel(f)}{f[CHARGE.quote] ? (quoteName(f[CHARGE.quote][0]) && quoteName(f[CHARGE.quote][0]) !== f[CHARGE.concept] ? ` · ${quoteName(f[CHARGE.quote][0])}` : ' · de la propuesta') : ' · directo'} · vence {f[CHARGE.dueOn]} · pendiente {fmt(c.state.pending, c.state.currency)}</label>
                  {on && <input type="number" min={0} step="0.01" className="input-field" style={{ width: 110, fontSize: 12 }} value={sel[c.id]} aria-label={`Aplicar a ${f[CHARGE.concept]}`} onChange={e => setSel(s => ({ ...s, [c.id]: e.target.value }))} />}
                </div>
              );
            })}
          </div>

          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', fontSize: 12 }}>
            <label>Importe <input type="number" min={0} step="0.01" className="input-field" style={{ width: 120, fontSize: 12 }} value={amount} onChange={e => setAmount(e.target.value)} aria-label="Importe del pago" /></label>
            <label>Moneda {currencies.length ? <b>{currency}</b> : <input className="input-field" style={{ width: 70, fontSize: 12 }} maxLength={3} value={currencyFree} onChange={e => setCurrencyFree(e.target.value.toUpperCase())} aria-label="Moneda" />}</label>
            <label>Fecha <input type="date" className="input-field" style={{ fontSize: 12 }} value={receivedOn} max={todayDay()} onChange={e => setReceivedOn(e.target.value)} aria-label="Fecha del pago" /></label>
            <label>Medio <input className="input-field" style={{ width: 140, fontSize: 12 }} value={method} placeholder="Transferencia…" onChange={e => setMethod(e.target.value)} aria-label="Medio" /></label>
            <label>Referencia <input className="input-field" style={{ width: 140, fontSize: 12 }} value={reference} onChange={e => setReference(e.target.value)} aria-label="Referencia" /></label>
            {pending.length > 0 && amountC > 0 && <button type="button" className="action-btn btn-ghost" style={{ fontSize: 11 }} onClick={spread}>Repartir por vencimiento</button>}
          </div>

          {amountC > 0 && (
            <div style={{ padding: 8, borderRadius: 8, background: 'rgba(30,42,44,0.03)', fontSize: 12 }}>
              <b>Así queda</b>
              {chosen.map(c => <div key={c.id}>{c.fields?.[CHARGE.concept]} · {kindLabel(c.fields || {})}: se aplican {fmt(Number(sel[c.id]) || 0, currency)} · queda pendiente {fmt(money(Math.max(cents(c.state.pending) - cents(Number(sel[c.id]) || 0), 0)), currency)}</div>)}
              <div>Sin aplicar: <b>{fmt(money(Math.max(unappliedC, 0)), currency)}</b>{unappliedC > 0 ? ' (queda disponible para aplicar después)' : ''}</div>
            </div>
          )}
          {problems.map(p => <div key={p} role="alert" style={{ fontSize: 12, color: '#B4362C' }}>{p}</div>)}
        </>
      )}
      {err && <div role="alert" style={{ fontSize: 12, color: '#B4362C' }}>{err}</div>}
      <div style={{ display: 'flex', gap: 6 }}>
        <button type="button" className="action-btn btn-primary" style={{ fontSize: 12 }} disabled={busy || !ready} onClick={submit}>{busy ? 'Registrando…' : 'Confirmar pago'}</button>
        <button type="button" className="action-btn btn-ghost" style={{ fontSize: 12 }} disabled={busy} onClick={onCancel}>Cancelar</button>
      </div>
      <div style={muted}>Se registra una sola vez. Al confirmarlo se emite su recibo interno (si están los datos del emisor); no se envía nada solo.</div>
    </div>
  );
}
